import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { authApi } from '@api/auth'
import { useAuthStore } from '@store/authStore'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'

/**
 * Authentication Hook
 */
export const useAuth = () => {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { login: setAuth, logout: clearAuth, user, isAuthenticated, token } = useAuthStore()

    // Login mutation
    const loginMutation = useMutation({
        mutationFn: ({ email, password }) => authApi.login(email, password),
        onSuccess: (response) => {
            const { token, user } = response.data
            setAuth(token, user)
            toast.success('Login successful')
            // Redirect based on user type
            if (user.user_type === 'scanner') {
                navigate('/ss/scanner')
            } else if (user.user_type === 'end_user') {
                navigate('/portal/dashboard')
            } else {
                navigate('/dashboard')
            }
        },
        onError: (error) => {
            const message = error.response?.data?.message || 'Login failed'
            toast.error(message)
        },
    })

    // Logout mutation
    const logoutMutation = useMutation({
        mutationFn: () => authApi.logout(),
        onSuccess: () => {
            clearAuth()
            queryClient.clear()
            toast.success('Logged out successfully')
            navigate('/login')
        },
        onError: () => {
            // Still logout on error
            clearAuth()
            queryClient.clear()
            navigate('/login')
        },
    })

    // Get current user query
    const { data: currentUser, isLoading: isLoadingUser } = useQuery({
        queryKey: ['currentUser'],
        queryFn: () => authApi.me(),
        enabled: isAuthenticated && !!token,
        staleTime: 5 * 60 * 1000, // 5 minutes
    })

    const login = async (email, password) => {
        return loginMutation.mutateAsync({ email, password })
    }

    const logout = () => {
        logoutMutation.mutate()
    }

    return {
        user: currentUser?.data || user,
        isAuthenticated,
        token,
        isLoading: loginMutation.isPending || isLoadingUser,
        isLoggingOut: logoutMutation.isPending,
        login,
        logout,
        loginError: loginMutation.error,
    }
}

export default useAuth
